import React, { useEffect, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { motion } from "framer-motion";
import { Laptop, Phone } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [dark, setDark] = useState(true);
  const navigate = useNavigate();

  /* ================= THEME SYNC ================= */
  useEffect(() => {
    const syncTheme = () => {
      const t = localStorage.getItem("theme");
      setDark(t !== "light");
    };
    syncTheme();
    window.addEventListener("theme-change", syncTheme);
    return () => window.removeEventListener("theme-change", syncTheme);
  }, []);

  /* ================= LOAD PRODUCTS ================= */
  useEffect(() => {
    getDocs(collection(db, "products")).then((snap) =>
      setProducts(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
  }, []);

  /* ================= THEME MAP ================= */
  const theme = {
    page: dark
      ? "bg-[#0b0b0b] text-white"
      : "bg-gray-100 text-gray-900",

    card: dark
      ? "bg-white/5 border-white/10 backdrop-blur-xl"
      : "bg-white border-gray-200",

    muted: dark ? "text-gray-400" : "text-gray-600",

    btn: dark
      ? "bg-sky-600 hover:bg-sky-700 text-white"
      : "bg-gray-900 hover:bg-gray-800 text-white",
  };

  return (
    <div className={`min-h-screen px-5 py-20 transition-colors ${theme.page}`}>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-7xl mx-auto"
      >
        {/* ================= HEADER ================= */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-3 mb-4">
            <Laptop size={34} />
            <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight">
              Laptop Store
            </h1>
          </div>

          <p className={`max-w-2xl mx-auto text-lg ${theme.muted}`}>
            Professionally sourced laptops for business, education, and personal
            productivity.
          </p>
        </div>

        {/* ================= PRODUCTS ================= */}
        {products.length === 0 ? (
          <div className={`text-center ${theme.muted}`}>
            No laptops available at the moment.
          </div>
        ) : (
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {products.map((p, i) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08 }}
                className={`rounded-3xl overflow-hidden shadow-xl transition ${theme.card}`}
              >
                {/* IMAGE */}
                <div className="relative">
                  <img
                    src={p.image}
                    alt={p.name}
                    className="h-56 w-full object-cover"
                  />

                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                </div>

                {/* BODY */}
                <div className="p-6">
                  <h3 className="text-xl font-semibold mb-2">
                    {p.name}
                  </h3>

                  <p className={`text-sm leading-relaxed ${theme.muted}`}>
                    {p.description}
                  </p>

                  {/* SPECS */}
                  {p.specs?.length > 0 && (
                    <ul className="mt-4 space-y-1 text-sm">
                      {p.specs.map((s, idx) => (
                        <li
                          key={idx}
                          className="flex items-center gap-2"
                        >
                          <span className="h-1.5 w-1.5 rounded-full bg-sky-500" />
                          {s}
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* CTA */}
                  <button
                    onClick={() => navigate("/contact")}
                    className={`mt-6 w-full py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition ${theme.btn}`}
                  >
                    <Phone size={16} /> Contact Seller
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </motion.div>
    </div>
  );
}
